import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  getAccessToken, setAccessToken, clearAccessToken,
  getRefreshToken, setRefreshToken, clearRefreshToken,
  parseJwtPayload, refreshTokens, markAuthInitComplete,
} from '../lib/auth';
import { loadGuestDraft, clearGuestDraft } from '../hooks/useGuestLimit';
import { finalizeInvoice } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Must survive a Render free-tier cold start (~30-60 s).
const AUTH_TIMEOUT_MS = 90_000;

const AuthContext = createContext({
  user: null,
  loading: true,
  requestOtp: async () => {},
  verifyOtp: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Kept only to cancel interval on unmount — no longer used for timer-based refresh.
  const intervalRef = useRef(null);

  // ── On mount: restore session from stored refresh token ───────────────────
  useEffect(() => {
    const initAuth = async () => {
      try {
        const data = await refreshTokens();
        if (data) {
          const payload = parseJwtPayload(data.access_token);
          setUser({ id: payload.sub, email: payload.email });
        }
      } catch {
        // Network error — token may still be valid; don't clear it.
      } finally {
        // Always signal that init is done so authFetch stops waiting.
        markAuthInitComplete();
        setLoading(false);
      }
    };

    initAuth();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Proactive token refresh (covers background-tab / computer-sleep) ───────
  // Runs every 60 s and also fires immediately when the tab becomes visible.
  // Refreshes when the access token has ≤ 2 minutes left. Because all refresh
  // calls go through the refreshTokens() singleton, concurrent triggers
  // (interval + visibilitychange at the same moment) share one network request.
  useEffect(() => {
    if (!user) return;

    const checkAndRefresh = async () => {
      const token = getAccessToken();
      const payload = token ? parseJwtPayload(token) : null;
      const msLeft = payload?.exp ? payload.exp * 1000 - Date.now() : 0;

      if (msLeft <= 2 * 60 * 1000) {
        const data = await refreshTokens();
        if (data) {
          const p = parseJwtPayload(data.access_token);
          if (p?.sub) setUser({ id: p.sub, email: p.email });
        } else {
          // Refresh token genuinely expired — sign out.
          setUser(null);
        }
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') checkAndRefresh();
    };

    document.addEventListener('visibilitychange', onVisible);
    intervalRef.current = setInterval(checkAndRefresh, 60_000);

    // Run once immediately so a token that expired while the tab was away
    // is caught without waiting a full 60 s.
    checkAndRefresh();

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(intervalRef.current);
    };
  }, [user]);

  // ── OTP request ───────────────────────────────────────────────────────────
  const requestOtp = async (email) => {
    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        signal: AbortSignal.timeout(AUTH_TIMEOUT_MS),
      });
    } catch (err) {
      if (err.name === 'TimeoutError' || err.name === 'AbortError') {
        throw new Error('Request timed out. The server may still be starting — please try again.');
      }
      throw new Error('Network error. Please check your connection and try again.');
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to send OTP');
    }
    return res.json();
  };

  // ── OTP verify / login ────────────────────────────────────────────────────
  const verifyOtp = async (email, otp) => {
    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
        signal: AbortSignal.timeout(AUTH_TIMEOUT_MS),
      });
    } catch (err) {
      if (err.name === 'TimeoutError' || err.name === 'AbortError') {
        throw new Error('Request timed out. The server may still be starting — please try again.');
      }
      throw new Error('Network error. Please check your connection and try again.');
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Invalid or expired OTP');
    }

    const data = await res.json();
    setAccessToken(data.access_token);
    setRefreshToken(data.refresh_token);
    setUser(data.user);

    // Claim any invoice the user created as a guest before registering.
    const draft = loadGuestDraft();
    if (draft) {
      try { await finalizeInvoice(draft); }
      catch (err) { console.error('Failed to claim guest invoice:', err); }
      finally { clearGuestDraft(); }
    }
  };

  // ── Sign out ──────────────────────────────────────────────────────────────
  const signOut = async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).catch(() => {});
    }

    if (intervalRef.current) clearInterval(intervalRef.current);
    clearAccessToken();
    clearRefreshToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, requestOtp, verifyOtp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
